import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { Badge, Button, Card, EmptyState, Field, inputStyle, tokens } from './ui/controls';

type VersionRow = {
  id?: number;
  version: string;
  s3_uri?: string | null;
  conversions?: Array<{ target: string; status: string; gpu_identity: string }>;
};

type ModelRow = {
  name: string;
  deploy_location?: string;
  versions: VersionRow[];
};

type ConversionRow = {
  id: number;
  name: string;
  version: string;
  status: string;
  gpu_identity: string;
  s3_uri?: string | null;
  trt_version?: string | null;
  error_message?: string | null;
};

function statusTone(status: string) {
  if (status === 'ready') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running' || status === 'queued') return 'warning';
  return 'neutral';
}

export default function ConversionsTab({ target }: { target: 'cloud' | 'embedded' }) {
  const [conversions, setConversions] = useState<ConversionRow[]>([]);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [versionId, setVersionId] = useState('');
  const [gpu, setGpu] = useState(target === 'cloud' ? 'ml.g4dn.xlarge' : '');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [listed, registry] = await Promise.all([
        api.autolabel.listConversions(target),
        api.dataviewer.modelRegistry(),
      ]);
      setConversions((listed.data.conversions || []) as ConversionRow[]);
      setModels((registry.data.models || []) as ModelRow[]);
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => { refresh().catch((e) => setStatus(String(e))); }, [refresh]);

  const versions = models.flatMap((model) =>
    (model.versions || [])
      .filter((version) => version.s3_uri && version.id)
      .map((version) => ({
        id: version.id as number,
        label: `${model.name} v${version.version}`,
      })),
  );

  const start = async () => {
    setStatus('');
    try {
      await api.autolabel.startConversion({
        model_version_id: Number(versionId),
        target,
        gpu_identity: gpu.trim(),
      });
      setVersionId('');
      await refresh();
    } catch (e: any) {
      setStatus(e.response?.data?.detail || e.message || 'Request failed');
    }
  };

  const title = target === 'cloud' ? 'Cloud conversions' : 'Embedded conversions';
  const gpuLabel = target === 'cloud' ? 'Instance type' : 'Jetson device';

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p style={{ marginTop: 0, fontSize: '0.88rem', color: tokens.textMuted, maxWidth: 720 }}>
        {target === 'cloud'
          ? 'A SageMaker job builds the engine on the instance type it will run on.'
          : 'DataViewer queues the conversion. The worker on that Jetson picks it up and uploads the engine.'}
      </p>
      {status && <p style={{ fontSize: '0.85rem', color: tokens.danger }}>{status}</p>}
      <Card title="Start a conversion">
        <div style={{ display: 'grid', gap: '0.75rem', maxWidth: 520 }}>
          <Field label="Trained model">
            <select style={inputStyle} value={versionId} onChange={(e) => setVersionId(e.target.value)}>
              <option value="">Select a version</option>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>{version.label}</option>
              ))}
            </select>
          </Field>
          <Field label={gpuLabel}>
            <input style={inputStyle} value={gpu} onChange={(e) => setGpu(e.target.value)} />
          </Field>
          <Button variant="primary" disabled={!versionId || !gpu.trim()} onClick={start}>
            Start conversion
          </Button>
        </div>
      </Card>
      <Card
        title="Engines"
        actions={<Button onClick={() => refresh().catch((e) => setStatus(String(e)))} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Button>}
      >
        {conversions.length === 0 ? (
          <EmptyState>No conversions for this target.</EmptyState>
        ) : (
          <div style={{ display: 'grid', gap: '0.45rem' }}>
            {conversions.map((row) => (
              <div key={row.id} style={{ border: `1px solid ${tokens.border}`, borderRadius: tokens.radius, padding: '0.6rem 0.8rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge tone={statusTone(row.status)}>{row.status}</Badge>
                  <strong style={{ fontSize: '0.92rem' }}>{row.name}</strong>
                  <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>v{row.version}</span>
                  <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>{row.gpu_identity}</span>
                </div>
                {row.s3_uri && <div style={{ fontSize: '0.8rem', marginTop: '0.3rem' }}>{row.s3_uri}</div>}
                {row.error_message && <div style={{ fontSize: '0.8rem', color: tokens.danger, marginTop: '0.3rem' }}>{row.error_message}</div>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
